/**
 * Running / done stats (Phase 65 / BLU-594 PR3b). A big-number stat strip
 * (products done/total, files uploaded, lookups upserted, metafields written)
 * over a progress bar, plus a searchable, bounded error log (Collapsible). The
 * error list is honest, not silent: `execute.ts` records every per-stage
 * failure into `stats.errors` and we surface all of them.
 *
 * The denominator comes from the fetched-once plan, so it can legitimately be
 * unknown for a moment (plan query in flight) or forever (a terminal job's plan
 * is nulled for retention). Unknown never renders as "of 0": mid-run it is an
 * indeterminate bar, and the copy drops the total.
 *
 * Store SKU set-up jobs (Phase 88, S18) write database rows only: no files,
 * no print rows, no metafields. Their stats carry `declared` instead, so they
 * show the products tile and one "variants set up" tile, plus the
 * changed-since-review and already-set-up counts when either is above 0. The
 * branch reads the stats shape (or the plan's own identity while the run has
 * not reported yet), so an older derived job never shows the new tile.
 *
 * Phase 92: a run on a 3PL's blank finishes before its print file name resync
 * (it runs detached), so the job completes with every such product pending.
 * Until the resync answers or its bound passes (`useAwaitingSupplyResync`),
 * that line reads "Updating print file names...", neutral, never the "run the
 * sync again" warning. The resync lines and the warnings sit in one polite
 * status region so the answer is announced. A job without the resync counters
 * renders exactly as before.
 */
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import * as Collapsible from '@radix-ui/react-collapsible';
import { ChevronRight, Loader2, Search } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Input } from '@/components/ui/input';
import { CoverageBar } from '../CoverageBar';
import { useCanOpenPlatenSettings } from '../../hooks/usePlatenSettingsReach';
import { useAwaitingSupplyResync } from '../../hooks/useAwaitingSupplyResync';
import { statNum, type JobStats, type ProductSyncJobStatus } from '../../lib/apply-types';

interface JobRunningStatsProps {
  stats: JobStats;
  /** From the plan. `0` means "not known" (plan still loading, or nulled on a finished job). */
  totalProducts: number;
  status: ProductSyncJobStatus;
  /**
   * A store SKU set-up job: the page's one answer (its plan's identity, or
   * once the plan is gone its stats), so a set-up job reads as one before its
   * stats land.
   */
  declared: boolean;
  /**
   * The job row's `finished_at`. Bounds the wait for a supply resync; absent
   * (or a job with no pending resync) reads exactly as before.
   */
  finishedAt?: string | null;
}

/**
 * Phase 92 (92-25, 92-27b): plain labels for the stages a run on a 3PL's
 * blank can record, in `errors` or `warnings`. Any other stage keeps the chip
 * it showed before (a non-supply job renders exactly as it did).
 */
const SUPPLY_STAGE_LABEL_KEYS: Record<string, string> = {
  supply_alias_ensure: 'job.stage.supply_alias_ensure',
  print_sku_in_use: 'job.stage.print_sku_in_use',
  supply_choice_changed: 'job.stage.supply_choice_changed',
  supply_check_failed: 'job.stage.supply_check_failed',
  supply_metadata_resync: 'job.stage.supply_metadata_resync',
  supply_storage: 'job.stage.supply_storage',
  coverage_changed_after_approval: 'job.stage.coverage_changed_after_approval',
};

function StatTile({ value, label }: { value: number; label: string }) {
  return (
    <div className="rounded-lg border bg-background px-4 py-3">
      <div className="text-2xl font-bold tabular-nums">{value}</div>
      <div className="mt-0.5 text-[11px] font-medium uppercase tracking-wide text-muted-foreground">{label}</div>
    </div>
  );
}

export function JobRunningStats({ stats, totalProducts, status, declared, finishedAt }: JobRunningStatsProps) {
  const { t } = useTranslation('productSync');
  const [filter, setFilter] = useState('');
  // An org that cannot open Production settings maps the platen info fields
  // in Settings, Integrations instead, so the hint must not send it there.
  const canOpenPlatenSettings = useCanOpenPlatenSettings();

  const totalKnown = totalProducts > 0;
  const complete = status === 'completed';
  // A set-up job writes in one pass and reports no per-product progress: once
  // it completes, every approved product is done.
  const productsDone = declared && complete && totalKnown ? totalProducts : statNum(stats, 'productsDone');
  // Finished with its plan already gone: there is no honest product count.
  const declaredProductsUnknown = declared && complete && !totalKnown;
  const variantsSetUp = statNum(stats, 'declared');
  const changedSinceReview = statNum(stats, 'changedSinceReview');
  // Already set up, whether before this run (the automatic set-up or an
  // upload, `alreadySetUp`) or by a parallel write while it ran
  // (`skippedAtWrite`): either way nothing was written for them, so one line.
  const alreadySetUp = statNum(stats, 'alreadySetUp') + statNum(stats, 'skippedAtWrite');
  const filesUploaded = statNum(stats, 'filesUploaded');
  const lookupsUpserted = statNum(stats, 'lookupsUpserted');
  const metafieldsWritten = statNum(stats, 'metafieldsWritten');
  const tencentSkipped = statNum(stats, 'tencentSkipped');
  const platenPublished = statNum(stats, 'platenVariantSkusPublished');
  const platenMissing = statNum(stats, 'platenVariantFilesMissing');
  const platenInfoWarnings = statNum(stats, 'platenInfoWarnings');
  const platenSmallMade = statNum(stats, 'platenSmallMade');
  const platenSmallKept = statNum(stats, 'platenSmallKept');
  const pct = totalKnown ? (productsDone / totalProducts) * 100 : complete ? 100 : 0;
  // Mid-run with no denominator yet: pulse the track rather than assert 0%.
  // A set-up job reports no per-product progress at all (one pass), so its
  // bar pulses for the whole run instead of sitting at 0% then jumping.
  const indeterminate = status === 'running' && (!totalKnown || declared);
  const progressLabel = totalKnown
    ? t('job.stats.progress', { done: productsDone, total: totalProducts })
    : t('job.stats.progressUnknown', { done: productsDone });

  // Phase 92 (92-25): only a run on a 3PL's blank carries these; absent reads 0.
  const supplyAliasesEnsured = statNum(stats, 'supplyAliasesEnsured');
  const supplyVariantsRefused = statNum(stats, 'supplyVariantsRefused');
  const supplyMetadataResynced = statNum(stats, 'supplyMetadataResynced');
  const supplyMetadataResyncPending = statNum(stats, 'supplyMetadataResyncPending');
  const warnings = stats.warnings ?? [];
  // The pending count is the resync warning's own number; it shows as a line
  // only when no such warning carries it.
  const resyncWarned = warnings.some((w) => w.stage === 'supply_metadata_resync');
  // The detached resync can still answer: neutral "Updating..." instead of the warning.
  const awaitingResync = useAwaitingSupplyResync(status, stats, finishedAt);
  // Only a run that wrote supply products carries the resync counters; only
  // then do its resync lines and warnings share a status region.
  const hasResync =
    typeof stats.supplyMetadataResyncPending === 'number' || typeof stats.supplyMetadataResynced === 'number';

  const errors = stats.errors ?? [];
  const filtered = useMemo(() => {
    const list = stats.errors ?? [];
    const q = filter.trim().toLowerCase();
    if (!q) return list;
    const label = (stage: string): string => {
      const key = SUPPLY_STAGE_LABEL_KEYS[stage];
      return key ? ` ${t(key)}` : '';
    };
    return list.filter((e) =>
      `${e.title ?? ''} ${e.productId} ${e.sku ?? ''} ${e.stage}${label(e.stage)} ${e.message}`.toLowerCase().includes(q),
    );
  }, [stats.errors, filter, t]);
  /** A supply stage's plain label; any other stage exactly as before. */
  const stageLabel = (stage: string): string => {
    const key = SUPPLY_STAGE_LABEL_KEYS[stage];
    return key ? t(key) : stage;
  };
  /**
   * A counted warning in the reader's language: the resync one from its
   * counter, the coverage "changed after approval" one from its own `count`.
   * Any other warning keeps its message.
   */
  const warningText = (w: { stage: string; message: string; count?: number }): string => {
    if (w.stage === 'supply_metadata_resync' && supplyMetadataResyncPending > 0) {
      return t('job.stats.supplyMetadataResyncPending', { count: supplyMetadataResyncPending });
    }
    if (w.stage === 'coverage_changed_after_approval' && typeof w.count === 'number' && w.count > 0) {
      return t('job.stats.coverageChangedAfterApproval', { count: w.count });
    }
    return w.message;
  };

  const resyncLines = (
    <>
      {supplyMetadataResynced > 0 && (
        <p className="text-xs text-muted-foreground">{t('job.stats.supplyMetadataResynced', { count: supplyMetadataResynced })}</p>
      )}
      {supplyMetadataResyncPending > 0 && !resyncWarned && awaitingResync && (
        <p className="flex items-start gap-1 text-xs text-muted-foreground">
          <Loader2 className="mt-0.5 h-3 w-3 shrink-0 animate-spin motion-reduce:animate-none" aria-hidden="true" />
          <span>{t('job.stats.supplyMetadataResyncWaiting')}</span>
        </p>
      )}
      {supplyMetadataResyncPending > 0 && !resyncWarned && !awaitingResync && (
        <p className="text-xs text-amber-700">{t('job.stats.supplyMetadataResyncPending', { count: supplyMetadataResyncPending })}</p>
      )}
      {warnings.length > 0 && (
        <ul className="space-y-1" data-testid="job-warnings">
          {warnings.map((w, i) => (
            <li key={i} className="rounded-md border border-amber-200 bg-amber-50/60 px-2.5 py-1.5">
              <span className="rounded bg-amber-100 px-1.5 py-0.5 text-[9.5px] font-semibold uppercase tracking-wide text-amber-800">
                {SUPPLY_STAGE_LABEL_KEYS[w.stage] ? stageLabel(w.stage) : t('job.warning.notice')}
              </span>
              <p className="mt-0.5 text-[11.5px] leading-normal text-foreground">{warningText(w)}</p>
            </li>
          ))}
        </ul>
      )}
    </>
  );

  return (
    <div className="space-y-4">
      {declared ? (
        // One or two tiles: the grid has exactly that many columns, so the
        // tiles fill the row instead of leaving empty slots beside them.
        <div className={cn('grid gap-3', declaredProductsUnknown ? 'grid-cols-1' : 'grid-cols-2')}>
          {!declaredProductsUnknown && (
            <StatTile
              value={productsDone}
              label={totalKnown ? t('job.stats.productsDone', { total: totalProducts }) : t('job.stats.productsDoneUnknown')}
            />
          )}
          <StatTile value={variantsSetUp} label={t('job.stats.variantsSetUp')} />
        </div>
      ) : (
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
          <StatTile
            value={productsDone}
            label={totalKnown ? t('job.stats.productsDone', { total: totalProducts }) : t('job.stats.productsDoneUnknown')}
          />
          <StatTile value={filesUploaded} label={t('job.stats.filesUploaded')} />
          <StatTile value={lookupsUpserted} label={t('job.stats.lookupsUpserted')} />
          <StatTile value={metafieldsWritten} label={t('job.stats.metafieldsWritten')} />
        </div>
      )}

      <div className="space-y-1.5">
        <CoverageBar value={pct} complete={complete} tone="progress" indeterminate={indeterminate} label={progressLabel} className="h-1.5" />
        {/* Live region: this page polls, so progress must not be silent to screen readers. */}
        <p className="text-xs tabular-nums text-muted-foreground" aria-live="polite">
          {declaredProductsUnknown ? '' : progressLabel}
        </p>
      </div>

      {/* Phase 88 (S18): set-up outcomes beyond the tile, only when non-zero. */}
      {declared && changedSinceReview > 0 && (
        <p className="text-xs text-amber-700">{t('job.stats.changedSinceReview', { count: changedSinceReview })}</p>
      )}
      {declared && alreadySetUp > 0 && (
        <p className="text-xs text-muted-foreground">{t('job.stats.skippedAtWrite', { count: alreadySetUp })}</p>
      )}

      {tencentSkipped > 0 && (
        <p className="text-xs text-amber-700">{t('job.stats.tencentSkipped', { count: tencentSkipped })}</p>
      )}

      {/* Phase 86 'By set' platen variant runs. */}
      {platenPublished > 0 && (
        <p className="text-xs text-muted-foreground">{t('job.stats.platenVariantSkusPublished', { count: platenPublished })}</p>
      )}
      {/* Round 4: right under the published line ("Of these" made counts only published SKUs). */}
      {platenSmallMade > 0 && (
        <p className="text-xs text-muted-foreground">{t('job.stats.platenSmallMade', { count: platenSmallMade })}</p>
      )}
      {platenSmallKept > 0 && (
        <p className="text-xs text-muted-foreground">{t('job.stats.platenSmallKept', { count: platenSmallKept })}</p>
      )}
      {platenMissing > 0 && (
        <p className="text-xs text-amber-700">{t('job.stats.platenVariantFilesMissing', { count: platenMissing })}</p>
      )}
      {platenInfoWarnings > 0 && (
        <p className="text-xs text-amber-700">
          {canOpenPlatenSettings
            ? t('job.stats.platenInfoWarnings', { count: platenInfoWarnings })
            : t('job.stats.platenInfoWarningsClient', { count: platenInfoWarnings })}
        </p>
      )}

      {/* Phase 92: a run on a 3PL's blank. Each line only when above 0. */}
      {supplyAliasesEnsured > 0 && (
        <p className="text-xs text-muted-foreground">{t('job.stats.supplyAliasesEnsured', { count: supplyAliasesEnsured })}</p>
      )}
      {supplyVariantsRefused > 0 && (
        <p className="text-xs text-amber-700">{t('job.stats.supplyVariantsRefused', { count: supplyVariantsRefused })}</p>
      )}
      {hasResync ? (
        // The resync answers after the job is final: one polite region (empty
        // and hidden once nothing is left to say) so the answer is announced.
        <div role="status" className="space-y-4 empty:hidden">
          {resyncLines}
        </div>
      ) : (
        resyncLines
      )}

      {errors.length > 0 && (
        <Collapsible.Root defaultOpen>
          <Collapsible.Trigger asChild>
            <button
              type="button"
              className="group flex w-full items-center gap-2 rounded-md border border-red-200 bg-red-50/60 px-3 py-2 text-left transition-colors duration-200 hover:bg-red-50 motion-reduce:transition-none"
            >
              <ChevronRight className="h-4 w-4 text-red-600 transition-transform duration-200 group-data-[state=open]:rotate-90 motion-reduce:transition-none" aria-hidden="true" />
              <span className="text-sm font-semibold text-red-800">{t('job.errors.title', { count: errors.length })}</span>
            </button>
          </Collapsible.Trigger>
          <Collapsible.Content>
            <div className="mt-2 space-y-2">
              <div className="relative">
                <Search className="absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" aria-hidden="true" />
                <Input value={filter} onChange={(e) => setFilter(e.target.value)} placeholder={t('job.errors.filterPlaceholder')} aria-label={t('job.errors.filterPlaceholder')} className="h-8 pl-8 text-xs" />
              </div>
              {filtered.length === 0 ? (
                <p className="px-1 py-2 text-xs text-muted-foreground">{t('job.errors.noneMatch')}</p>
              ) : (
                <ul className="max-h-72 space-y-1 overflow-y-auto">
                  {filtered.map((e, i) => (
                    <li key={i} className="rounded-md border px-2.5 py-1.5">
                      <div className="flex flex-wrap items-center gap-1.5">
                        <span className={cn('rounded px-1.5 py-0.5 text-[9.5px] font-semibold uppercase tracking-wide', 'bg-red-100 text-red-700')}>{stageLabel(e.stage)}</span>
                        {e.title && <span className="truncate text-[11.5px] font-semibold" title={e.title}>{e.title}</span>}
                        {e.sku && <span className="font-mono text-[11px] text-muted-foreground">{e.sku}</span>}
                        <span className="truncate font-mono text-[10.5px] text-muted-foreground/70" title={e.productId}>{e.productId}</span>
                      </div>
                      <p className="mt-0.5 text-[11.5px] text-foreground">{e.message}</p>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </Collapsible.Content>
        </Collapsible.Root>
      )}
    </div>
  );
}
